import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiRefreshCw, FiSearch, FiTrendingUp, FiTrendingDown, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { usePageHeader } from "../hooks/usePageHeader";
import { client } from "../Clients";
import { formatRelativeTime } from "../Helper/dates";
import { LoadingSpinner } from "../Components/LoadingSpinner";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";

// Local mirror of the GET /api/exchange-rates payload. The generated client types this response
// loosely, so the shape is pinned here and every field is defaulted in applyResponse : a partial
// payload degrades to "-" cells instead of a crash.
type RateView = {
  currencyCode: string;
  currencyName: string;
  flagCode: string;
  unite: number | null;
  buyRate: number | null;
  sellRate: number | null;
  averageRate: number | null;
  change1d: number | null;
  change7d: number | null;
  date: string;
};

type HistoryPoint = {
  date: string;
  buyRate: number | null;
  sellRate: number | null;
  averageRate: number | null;
};

type RatesData = {
  generatedAt: string;
  lastSyncedAt: string | null;
  rates: RateView[];
  series: Record<string, HistoryPoint[]>;
  flags: Record<string, string>;
};

type OfficeView = {
  code: string;
  acronym: string;
  name: string;
  institutionType: number;
  status: string;
};

const fmt = (v: number | null | undefined, decimals = 2) =>
  v != null && !isNaN(v) ? v.toFixed(decimals) : "-";

const pct = (v: number | null | undefined) => {
  if (v == null || isNaN(v)) return null;
  const p = v * 100;
  return { value: p, label: `${p >= 0 ? "+" : ""}${p.toFixed(2)}%`, positive: p >= 0 };
};

const HIGHLIGHT_CURRENCIES = ["USD", "EUR", "ZAR", "GBP", "CNY", "CHF"];

/**
 * CDF exchange-rate dashboard: highlight cards for the major currencies, a 7-day buy/sell/average
 * chart for the selected currency, the full searchable currency table with sparklines, and the
 * roster of registered exchange offices. Rates and offices come from the server's DB cache
 * (GET /api/exchange-rates + /api/exchange-rates/offices); POST /api/exchange-rates/sync asks the
 * server to re-pull the slow upstream feed. UX contract: paint immediately from the cache, then
 * refresh via a silent background sync : the operator only ever waits on an explicit "Sync Now",
 * and a failed background sync is swallowed because stale cached rates are still worth showing.
 */
const ExchangeRates: React.FC = () => {
  usePageHeader({ title: "Exchange Rates" });

  const [data, setData] = useState<RatesData | null>(null);
  const [offices, setOffices] = useState<OfficeView[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [activeCurrency, setActiveCurrency] = useState("USD");
  const [showOffices, setShowOffices] = useState(false);

  const applyResponse = useCallback((rd: any, od: any) => {
    setData({
      generatedAt: rd?.generatedAt ?? new Date().toISOString(),
      lastSyncedAt: rd?.lastSyncedAt ?? null,
      rates: rd?.rates ?? [],
      series: rd?.series ?? {},
      flags: rd?.flags ?? {}
    });
    if (od) setOffices(od?.offices ?? []);
  }, []);

  /** Fast read from database cache. */
  const fetchCached = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ratesRes, officesRes] = await Promise.all([
        client.exchangeRates.apiExchangeRatesGet(),
        client.exchangeRates.apiExchangeRatesOfficesGet()
      ]);
      applyResponse(ratesRes.data, officesRes.data);
    } catch (err) {
      console.error(err);
      setError("Unable to load exchange rates.");
    } finally {
      setLoading(false);
    }
  }, [applyResponse]);

  /** Background sync: calls the slow external API, then refreshes cached data. */
  const backgroundSync = useCallback(async () => {
    setSyncing(true);
    try {
      await client.exchangeRates.apiExchangeRatesSyncPost();
      // Refresh with new data after sync completes
      const [ratesRes, officesRes] = await Promise.all([
        client.exchangeRates.apiExchangeRatesGet(),
        client.exchangeRates.apiExchangeRatesOfficesGet()
      ]);
      applyResponse(ratesRes.data, officesRes.data);
    } catch (err) {
      // Sync failure is non-blocking : cached data is still shown
      console.warn("Background sync failed:", err);
    } finally {
      setSyncing(false);
    }
  }, [applyResponse]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setError(null);
    try {
      await client.exchangeRates.apiExchangeRatesSyncPost();
      await fetchCached();
    } catch (err: any) {
      console.error(err);
      setError("Sync failed. The external API may be slow : try again in a few minutes.");
    } finally {
      setSyncing(false);
    }
  }, [fetchCached]);

  // On mount: fast cached read, then background sync. The ref makes the pair run strictly once : // without it, dev double-invocation (StrictMode) or any future dep-identity change would kick off
  // a second sync against the slow upstream API.
  const mountedRef = React.useRef(false);
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    fetchCached().then(() => {
      backgroundSync();
    });
  }, [fetchCached, backgroundSync]);

  const filteredRates = useMemo(() => {
    if (!data?.rates) return [];
    const q = search.toLowerCase().trim();
    if (!q) return data.rates;
    return data.rates.filter(r =>
      r.currencyCode.toLowerCase().includes(q) ||
      r.currencyName.toLowerCase().includes(q)
    );
  }, [data?.rates, search]);

  const highlightRates = useMemo(() => {
    if (!data?.rates) return [];
    return HIGHLIGHT_CURRENCIES
      .map(code => data.rates.find(r => r.currencyCode === code))
      .filter(Boolean) as RateView[];
  }, [data?.rates]);

  const activeSeries = useMemo(() => {
    if (!data?.series?.[activeCurrency]) return null;
    const points = data.series[activeCurrency];
    if (points.length === 0) return null;
    return {
      dates: points.map(p => p.date?.slice(0, 10) ?? ""),
      buy: points.map(p => p.buyRate ?? 0),
      sell: points.map(p => p.sellRate ?? 0),
      avg: points.map(p => p.averageRate ?? 0)
    };
  }, [data?.series, activeCurrency]);

  const chartOptions: ApexOptions = useMemo(() => ({
    chart: { type: "area", toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit", background: "transparent" },
    colors: ["#3b82f6", "#f59e0b", "#10b981"],
    dataLabels: { enabled: false },
    // `straight` draws line segments between data points so each daily
    // move is visible : `smooth` was averaging spikes out and hiding
    // small day-to-day movements that the FX desk specifically watches.
    stroke: { curve: "straight", width: 2 },
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05, stops: [0, 100] } },
    xaxis: { categories: activeSeries?.dates ?? [], labels: { style: { fontSize: "11px", colors: "#94a3b8" } } },
    yaxis: { labels: { style: { fontSize: "11px", colors: "#94a3b8" }, formatter: (v: number) => v.toFixed(2) } },
    tooltip: { theme: "dark", y: { formatter: (v: number) => v.toFixed(4) } },
    legend: { position: "top", horizontalAlign: "right", fontSize: "12px", fontWeight: 600 },
    grid: { borderColor: "#e2e8f0", strokeDashArray: 4 }
  }), [activeSeries]);

  const chartSeries = useMemo(() => {
    if (!activeSeries) return [];
    return [
      { name: "Achat (Buy)", data: activeSeries.buy },
      { name: "Vente (Sell)", data: activeSeries.sell },
      { name: "Moyen (Avg)", data: activeSeries.avg }
    ];
  }, [activeSeries]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <LoadingSpinner size="lg" />
          <p className="text-sm text-slate-500">Loading exchange rates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full space-y-6 overflow-x-hidden p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Exchange Rates</h1>
          <p className="text-sm text-slate-500">
            {data?.lastSyncedAt
              ? `Last synced ${formatRelativeTime(data.lastSyncedAt)}`
              : "No data synced yet. Click Sync to fetch rates."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={fetchCached}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
          >
            <FiRefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:bg-slate-400"
          >
            {syncing ? <LoadingSpinner size="sm" inline /> : <FiRefreshCw className="h-4 w-4" />}
            {syncing ? "Syncing..." : "Sync Now"}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      )}

      {(!data || data.rates.length === 0) && !error && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center">
          <p className="text-lg font-semibold text-slate-700">No exchange rate data available</p>
          <p className="mt-2 text-sm text-slate-500">Click "Sync Now" to fetch rates from the Central Bank API. The first sync may take a minute.</p>
        </div>
      )}

      {data && data.rates.length > 0 && (
        <>
          {/* Summary cards */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {highlightRates.map(rate => {
              const change = pct(rate.change1d);
              const flag = data.flags[rate.currencyCode];
              return (
                <button
                  key={rate.currencyCode}
                  type="button"
                  onClick={() => setActiveCurrency(rate.currencyCode)}
                  className={`rounded-xl border p-4 text-left shadow-sm transition hover:shadow-md ${
                    activeCurrency === rate.currencyCode
                      ? "border-blue-300 bg-blue-50 ring-1 ring-blue-200"
                      : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {flag && <span className={`fi fi-${flag} h-4 w-5 rounded-sm`} />}
                    <span className="text-sm font-bold text-slate-900">{rate.currencyCode}</span>
                  </div>
                  <p className="mt-2 text-xl font-bold text-slate-900">{fmt(rate.averageRate, 4)}</p>
                  <p className="text-[11px] text-slate-500">CDF</p>
                  {change && (
                    <div className={`mt-1 flex items-center gap-1 text-xs font-semibold ${change.positive ? "text-emerald-600" : "text-rose-600"}`}>
                      {change.positive ? <FiTrendingUp className="h-3 w-3" /> : <FiTrendingDown className="h-3 w-3" />}
                      {change.label}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Main chart */}
          {activeSeries && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">
                    {activeCurrency} / CDF
                    <span className="ml-2 text-sm font-normal text-slate-500">7-day evolution</span>
                  </h2>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  {data.rates.slice(0, 12).map(r => (
                    <button
                      key={r.currencyCode}
                      type="button"
                      onClick={() => setActiveCurrency(r.currencyCode)}
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${
                        activeCurrency === r.currencyCode
                          ? "bg-slate-900 text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {r.currencyCode}
                    </button>
                  ))}
                </div>
              </div>
              <Chart options={chartOptions} series={chartSeries} type="area" height={320} />
            </div>
          )}

          {/* Rate table */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5 sm:py-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">All Currencies</h2>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search currency..."
                  className="rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-700 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                    <th className="px-4 py-3">Currency</th>
                    <th className="px-4 py-3 text-right">Buy (Achat)</th>
                    <th className="px-4 py-3 text-right">Sell (Vente)</th>
                    <th className="px-4 py-3 text-right">Average</th>
                    <th className="px-4 py-3 text-right">1d Change</th>
                    <th className="px-4 py-3 text-right">7d Change</th>
                    <th className="px-4 py-3 text-right">Trend</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredRates.map(rate => {
                    const c1d = pct(rate.change1d);
                    const c7d = pct(rate.change7d);
                    const flag = data.flags[rate.currencyCode];
                    const series = data.series[rate.currencyCode];
                    const sparkData = series?.map(p => p.averageRate ?? 0) ?? [];
                    const isActive = activeCurrency === rate.currencyCode;
                    return (
                      <tr
                        key={rate.currencyCode}
                        onClick={() => setActiveCurrency(rate.currencyCode)}
                        className={`cursor-pointer transition ${isActive ? "bg-blue-50" : "hover:bg-slate-50"}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            {flag && <span className={`fi fi-${flag} h-4 w-5 rounded-sm`} />}
                            <div>
                              <p className="font-semibold text-slate-900">{rate.currencyCode}</p>
                              <p className="text-xs text-slate-500">{rate.currencyName}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{fmt(rate.buyRate, 4)}</td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{fmt(rate.sellRate, 4)}</td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{fmt(rate.averageRate, 4)}</td>
                        <td className="px-4 py-3 text-right">
                          {c1d ? (
                            <span className={`inline-flex items-center gap-1 font-semibold ${c1d.positive ? "text-emerald-600" : "text-rose-600"}`}>
                              {c1d.positive ? <FiTrendingUp className="h-3 w-3" /> : <FiTrendingDown className="h-3 w-3" />}
                              {c1d.label}
                            </span>
                          ) : <span className="text-slate-400">-</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {c7d ? (
                            <span className={`inline-flex items-center gap-1 font-semibold ${c7d.positive ? "text-emerald-600" : "text-rose-600"}`}>
                              {c7d.positive ? <FiTrendingUp className="h-3 w-3" /> : <FiTrendingDown className="h-3 w-3" />}
                              {c7d.label}
                            </span>
                          ) : <span className="text-slate-400">-</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {sparkData.length > 1 ? (
                            <MiniSparkline data={sparkData} positive={sparkData[sparkData.length - 1] >= sparkData[0]} />
                          ) : <span className="text-slate-400">-</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filteredRates.length === 0 && (
              <div className="px-6 py-10 text-center text-sm text-slate-500">No currencies match your search.</div>
            )}
          </div>

          {/* Exchange offices */}
          {offices.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <button
                type="button"
                onClick={() => setShowOffices(!showOffices)}
                className="flex w-full items-center justify-between px-5 py-4 text-left"
              >
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Exchange Offices</h2>
                  <p className="text-xs text-slate-400">{offices.length} registered institutions</p>
                </div>
                {showOffices ? <FiChevronUp className="h-5 w-5 text-slate-400" /> : <FiChevronDown className="h-5 w-5 text-slate-400" />}
              </button>
              {showOffices && (
                <div className="overflow-x-auto border-t border-slate-100">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                        <th className="px-4 py-3">Code</th>
                        <th className="px-4 py-3">Acronym</th>
                        <th className="px-4 py-3">Name</th>
                        <th className="px-4 py-3 text-center">Type</th>
                        <th className="px-4 py-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {offices.map(office => (
                        <tr key={office.code} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-900">{office.code}</td>
                          <td className="px-4 py-3 font-semibold text-slate-700">{office.acronym}</td>
                          <td className="px-4 py-3 text-slate-600">{office.name}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600">
                              {office.institutionType}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                              office.status === "A"
                                ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                                : "border border-slate-200 bg-slate-50 text-slate-600"
                            }`}>
                              {office.status === "A" ? "Active" : office.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/** Mini sparkline rendered with SVG. */
const MiniSparkline: React.FC<{ data: number[]; positive: boolean }> = ({ data, positive }) => {
  const w = 60;
  const h = 20;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`).join(" ");
  const color = positive ? "#10b981" : "#ef4444";
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="inline-block">
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
    </svg>
  );
};

export default ExchangeRates;
